import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Calendar, Truck, Play, CheckCircle2, FileText, DollarSign,
  ChevronRight, ChevronDown, MapPin, Mail, Phone, BellOff, Edit2, Trash2,
  Send, Printer, Download, Plus, X, Camera, ImageOff, RotateCcw, Loader2,
  Lock, Save, Calculator, TrendingUp, PieChart as PieIcon, UserCog, AlertTriangle,
  Bell, Clock, MessageSquare,
} from "lucide-react";
import api, { formatErr, fileUrl } from "../../lib/apiClient";
import { fmtCAD } from "../../lib/format";
import { LineItemsEditor } from "../../components/LineItemsEditor";
import CustomerPicker from "../../components/CustomerPicker";
import ConfirmDialog from "../../components/ConfirmDialog";
import { MadeForYouPanel } from "../../components/jobDetail/MadeForYouPanel";
import { SidebarAttachments } from "../../components/jobDetail/SidebarAttachments";
import { SignaturePad } from "../../components/jobDetail/SignaturePad";

const HST_DEFAULT = 13;

const STATUS_COLOR = {
  Scheduled: "bg-sky-100 text-sky-800",
  "On My Way": "bg-indigo-100 text-indigo-800",
  "In Progress": "bg-amber-100 text-amber-900",
  Completed: "bg-emerald-100 text-emerald-800",
  Cancelled: "bg-slate-100 text-slate-700",
};

function fmtDateTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-CA", {
      day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}
function fmtShortTime(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("en-CA", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

/** Try to obtain coords for a quick map; falls back to the address string only. */
function osmEmbedUrl(address) {
  if (!address) return null;
  // OpenStreetMap doesn't have a free embed-by-query API like Google.
  // Use Nominatim search redirect URL — most browsers handle the iframe rendering via
  // a static "embed" tile using bbox derived from a geocode would require an API call.
  // Practical compromise: use a small static-style map via openstreetmap.org "export" iframe with q=.
  // The query string approach via openstreetmap.org/?query= renders the marker after navigation.
  // Simpler & reliable: render Leaflet via iframe wrapper isn't possible without JS. Use Maps embed via Nominatim+staticmap.
  // We'll use https://www.openstreetmap.org/export/embed.html?bbox=... but bbox needs lat/lng.
  // → Two-stage: client geocodes via Nominatim then computes bbox. Done in component effect.
  return null; // computed in effect
}

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [linkedInv, setLinkedInv] = useState(null);
  const [linkedEsts, setLinkedEsts] = useState([]);
  const [busy, setBusy] = useState({ step: null });
  const [err, setErr] = useState("");
  const [openSummary, setOpenSummary] = useState(true);
  const [openTech, setOpenTech] = useState(true);
  const [coords, setCoords] = useState(null); // {lat,lng}
  const [hstRate, setHstRate] = useState(13);
  const [settings, setSettings] = useState(null);
  const [corpTaxRate, setCorpTaxRate] = useState(12.2);

  // Inline line items (mirrored from linkedInv but editable in place)
  const [items, setItems] = useState([]);
  const [discType, setDiscType] = useState("amount");
  const [discVal, setDiscVal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Job inputs (private — costs of materials/misc used)
  const [jobInputs, setJobInputs] = useState([]);
  const [inputsDirty, setInputsDirty] = useState(false);
  const [inputsSaving, setInputsSaving] = useState(false);

  // Private notes
  const [privateNotes, setPrivateNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);

  // Change customer
  const [showChangeCustomer, setShowChangeCustomer] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const [changingCust, setChangingCust] = useState(false);

  // OMW notification dialog
  const [showOmwDialog, setShowOmwDialog] = useState(false);
  const [omwForm, setOmwForm] = useState({ send_notification: true, eta_minutes: 30, tech_note: "" });
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [payForm, setPayForm] = useState({
    amount: "", method: "cash", reference: "",
    paid_date: new Date().toISOString().split("T")[0],
  });

  const load = async () => {
    const [{ data: j }, { data: s }] = await Promise.all([
      api.get(`/jobs/${id}`), api.get("/settings"),
    ]);
    setJob(j);
    setSettings(s);
    setHstRate(Number(s?.hst_rate) || HST_DEFAULT);
    setCorpTaxRate(Number(s?.ontario_corp_tax_rate) || 12.2);
    setPrivateNotes(j.private_notes || "");
    setJobInputs(j.job_inputs || []);
    setNotesDirty(false); setInputsDirty(false);
    const [{ data: c }, { data: emps }] = await Promise.all([
      api.get(`/customers/${j.customer_id}`).catch(() => ({ data: null })),
      api.get("/employees"),
    ]);
    setCustomer(c); setEmployees(emps);

    // Linked invoice — under unified arch the job IS the invoice; always
    // fetch using job.id which `resolve_invoice_to_job` understands.
    try {
      const { data: inv } = await api.get(`/invoices/${j.id}`);
      if (inv && (inv.line_items?.length || (inv.total || 0) > 0 || inv.invoice_status)) {
        setLinkedInv(inv);
        setItems(inv.line_items || []);
        setDiscType(inv.invoice_discount_type || "amount");
        setDiscVal(inv.invoice_discount_value || 0);
      } else {
        // No invoice content yet — pre-fill items from job materials
        setLinkedInv(null);
        const fromMaterials = (j.materials || []).map((m) => ({
          id: m.id || crypto.randomUUID(),
          name: m.name, description: m.description || "",
          quantity: Number(m.quantity || 1), rate: Number(m.sale_price || 0),
          unit: m.unit || "ea", taxable: !!m.taxable,
          line_discount_type: "amount", line_discount_value: 0,
          pricebook_item_id: m.pricebook_item_id || null,
        }));
        setItems(fromMaterials);
      }
    } catch {
      setLinkedInv(null);
    }

    // Linked estimates (by customer history filtered to this job's estimate_id)
    try {
      if (!j.customer_id) return;
      const { data: hist } = await api.get(`/customers/${j.customer_id}/history`);
      setLinkedEsts((hist.estimates || []).filter((e) => !j.estimate_id || e.id === j.estimate_id || true).slice(0, 5));
    } catch {}
  };

  useEffect(() => { load(); }, [id]);

  // Geocode the service address client-side via Nominatim (1 request per job load, courteous)
  useEffect(() => {
    const addr = job?.service_address || customer?.billing_address;
    if (!addr) { setCoords(null); return; }
    const ctrl = new AbortController();
    fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(addr)}`,
      { signal: ctrl.signal, headers: { "Accept-Language": "en" } })
      .then((r) => r.ok ? r.json() : [])
      .then((arr) => arr?.[0] && setCoords({ lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon) }))
      .catch(() => {});
    return () => ctrl.abort();
  }, [job?.service_address, customer?.billing_address]);

  // Part 23 (Feb 2026): workflow buttons are pure internal time-tracking.
  // No geolocation prompt — the timestamp alone is what matters. Kept as a
  // no-op function for compatibility with the OMW dialog and workflow-reset
  // callers that used to pass geo along.
  const captureGeo = () => Promise.resolve({});

  const runStep = async (step) => {
    if (step === "invoice") return sendInvoice();  // workflow bar INVOICE button is a send-shortcut
    if (step === "omw") { setShowOmwDialog(true); return; } // open notification dialog
    if (step === "pay") {
      // Default to remaining balance
      setPayForm((p) => ({ ...p, amount: balanceDue || "" }));
      setShowPayDialog(true);
      return;
    }  // item 3: PAY opens payment modal
    setBusy({ step }); setErr("");
    try {
      const geo = await captureGeo();
      const { data: updated } = await api.post(`/jobs/${id}/workflow/${step}`, geo);
      setJob(updated);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy({ step: null }); }
  };

  const recordPayment = async () => {
    if (!linkedInv) return;
    if (!payForm.amount || Number(payForm.amount) <= 0) { setErr("Enter a payment amount"); return; }
    setBusy({ step: "pay" }); setErr("");
    try {
      await api.post(`/invoices/${linkedInv.id}/payments`, {
        amount: Number(payForm.amount),
        method: payForm.method,
        reference: payForm.reference || "",
        paid_date: payForm.paid_date,
      });
      // Refresh both the linked invoice and the job (activity + payments live on the job)
      const [{ data: inv }, { data: j }] = await Promise.all([
        api.get(`/invoices/${linkedInv.id}`),
        api.get(`/jobs/${id}`),
      ]);
      setLinkedInv(inv); setJob(j);
      setShowPayDialog(false);
      setPayForm((p) => ({ ...p, amount: "", reference: "" }));
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy({ step: null }); }
  };

  const submitOmw = async () => {
    setBusy({ step: "omw" }); setErr("");
    try {
      const geo = await captureGeo();
      const { data: updated } = await api.post(`/jobs/${id}/workflow/omw`, {
        ...geo,
        send_notification: omwForm.send_notification,
        eta_minutes: omwForm.eta_minutes ? Number(omwForm.eta_minutes) : null,
        tech_note: omwForm.tech_note || "",
      });
      setJob(updated);
      setShowOmwDialog(false);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy({ step: null }); }
  };
  const resetStep = (step) => {
    setDlg({
      title: `Reset "${step}" timestamp?`,
      body: `The ${step} timestamp will be cleared and the workflow step returned to its prior state.`,
      confirmLabel: "Reset",
      danger: false,
      onConfirm: async () => {
        try {
          const { data: updated } = await api.post(`/jobs/${id}/workflow-reset?step=${step}`);
          setJob(updated);
        } catch (e) { setErr(formatErr(e)); }
      },
    });
  };

  const balanceDue = useMemo(() => {
    if (!linkedInv) return null;
    return Math.max(Number(linkedInv.total || 0) - Number(linkedInv.amount_paid || 0), 0);
  }, [linkedInv]);

  const fullyPaid = linkedInv && linkedInv.status === "Paid";

  const saveLineItems = async (silent = false) => {
    if (!silent) setSaving(true);
    setErr("");
    try {
      const payload = {
        customer_id: job.customer_id,
        line_items: items,
        invoice_discount_type: discType,
        invoice_discount_value: Number(discVal) || 0,
        payment_terms: linkedInv?.payment_terms || "C.O.D.",
        status: linkedInv?.status || "Draft",
        notes: linkedInv?.notes || "",
      };
      // PRD Part 16: the job IS the invoice. Always PUT against the current
      // job id — never POST /api/invoices (that creates a duplicate row).
      const { data: inv } = await api.put(`/invoices/${id}`, payload);
      setLinkedInv(inv);
      setItems(inv.line_items || []);
      setSavedAt(new Date());
      if (!silent) {
        // refresh job to pick up activity log entries + status changes
        const { data: j } = await api.get(`/jobs/${id}`);
        setJob(j);
      }
      return inv;
    } catch (e) {
      if (!silent) setErr(formatErr(e));
      throw e;
    }
    finally { if (!silent) setSaving(false); }
  };

  // Auto-save inline invoice line items 1.5s after last edit (silent)
  useEffect(() => {
    if (!job) return;
    if (!items || items.length === 0) return;
    // Skip when no actual change since linkedInv (avoid POST on first render of empty doc)
    const same = linkedInv
      && JSON.stringify(linkedInv.line_items || []) === JSON.stringify(items)
      && (linkedInv.invoice_discount_type || "amount") === discType
      && Number(linkedInv.invoice_discount_value || 0) === Number(discVal || 0);
    if (same) return;
    const t = setTimeout(() => { saveLineItems(true).catch(() => {}); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, discType, discVal]);

  const finalizeInvoice = async () => {
    // Save any pending line-item changes, then route to the send-preview page.
    try {
      let inv = linkedInv;
      const dirty = !inv
        || JSON.stringify(inv.line_items || []) !== JSON.stringify(items)
        || (inv.invoice_discount_type || "amount") !== discType
        || Number(inv.invoice_discount_value || 0) !== Number(discVal || 0);
      if (dirty) inv = await saveLineItems(false);
      if (!inv) return;
      nav(`/invoices/${inv.id}/send`);
    } catch (e) { setErr(formatErr(e)); }
  };

  const sendInvoice = async () => {
    if (!linkedInv) { alert("Save line items first to create the invoice."); return; }
    // Save any pending changes before navigating to preview so the preview reflects
    // the latest line items.
    try {
      const dirty = JSON.stringify(linkedInv.line_items || []) !== JSON.stringify(items)
        || (linkedInv.invoice_discount_type || "amount") !== discType
        || Number(linkedInv.invoice_discount_value || 0) !== Number(discVal || 0);
      if (dirty) { await saveLineItems(true); }
    } catch (e) { setErr(formatErr(e)); return; }
    nav(`/invoices/${linkedInv.id}/send`);
  };

  // Part 20 (Feb 2026): manual send-reminder for outstanding invoices.
  const [remindBusy, setRemindBusy] = useState(false);
  const sendReminder = async () => {
    if (!linkedInv) return;
    if (!customer?.email) { setErr("Customer has no email address on file"); return; }
    setDlg({
      title: "Send payment reminder?",
      body: `A reminder email will be sent to ${customer.email} for invoice #${linkedInv.number || linkedInv.id}.`,
      confirmLabel: "Send reminder",
      danger: false,
      onConfirm: async () => {
        setRemindBusy(true);
        try {
          await api.post(`/invoices/${linkedInv.id}/remind`);
          await load();
        } catch (e) { setErr(formatErr(e)); }
        finally { setRemindBusy(false); }
      },
    });
  };

  const del = () => {
    setDlg({
      title: `Delete job ${job?.number || ""}?`,
      body: "This permanently deletes the job and its invoice. This cannot be undone.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await api.delete(`/jobs/${id}`);
          nav("/jobs");
        } catch (e) { alert(formatErr(e)); }
      },
    });
  };

  const buildJobPayload = (overrides = {}) => {
    const p = { ...job, ...overrides };
    delete p.id; delete p._id;
    delete p.time_entries; delete p.expenses;
    delete p.photos; delete p.private_photos;
    delete p.omw_at; delete p.omw_location;
    delete p.started_at; delete p.start_location;
    delete p.finished_at; delete p.finish_location;
    delete p.invoiced_at; delete p.created_at; delete p.updated_at;
    delete p.number;
    return p;
  };

  const savePrivateNotes = async () => {
    setNotesSaving(true);
    try {
      const { data: updated } = await api.put(`/jobs/${id}`, buildJobPayload({ private_notes: privateNotes }));
      setJob(updated); setNotesDirty(false);
    } catch (e) { setErr(formatErr(e)); }
    finally { setNotesSaving(false); }
  };

  const saveJobInputs = async () => {
    setInputsSaving(true);
    try {
      const cleaned = jobInputs.map((it) => ({ ...it, id: it.id || crypto.randomUUID() }));
      const { data: updated } = await api.put(`/jobs/${id}`, buildJobPayload({ job_inputs: cleaned }));
      setJob(updated); setJobInputs(updated.job_inputs || cleaned); setInputsDirty(false);
    } catch (e) { setErr(formatErr(e)); }
    finally { setInputsSaving(false); }
  };

  // Item 6: auto-save private notes (1.5s after last keystroke)
  useEffect(() => {
    if (!notesDirty || !id) return;
    const t = setTimeout(() => { savePrivateNotes(); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privateNotes, notesDirty, id]);

  // Item 6: auto-save job inputs (1.5s after last change)
  useEffect(() => {
    if (!inputsDirty || !id) return;
    const t = setTimeout(() => { saveJobInputs(); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobInputs, inputsDirty, id]);

  const openChangeCustomer = async () => {
    setShowChangeCustomer(true);
    if (allCustomers.length === 0) {
      const { data } = await api.get("/customers");
      setAllCustomers(data);
    }
  };

  const changeCustomer = (newId) => {
    setDlg({
      title: "Reassign to new customer?",
      body: "This job (and its linked invoice) will be reassigned to the selected customer.",
      confirmLabel: "Reassign",
      danger: false,
      onConfirm: async () => {
        setChangingCust(true);
        try {
          await doChangeCustomer(newId);
        } finally { setChangingCust(false); }
      },
    });
  };

  const doChangeCustomer = async (newId) => {
    try {
      // Under unified arch, job.id === invoice.id and both endpoints resolve to the same
      // record. Only issue the invoice-side PUT if there IS invoice content (line items
      // or a linked invoice reshape). Otherwise a plain job PUT is enough.
      if (linkedInv) {
        const invPayload = {
          customer_id: newId,
          line_items: linkedInv.line_items || [],
          invoice_discount_type: linkedInv.invoice_discount_type || "amount",
          invoice_discount_value: linkedInv.invoice_discount_value || 0,
          payment_terms: linkedInv.payment_terms || "Upon Receipt",
          status: linkedInv.status,
          notes: linkedInv.notes || "",
        };
        await api.put(`/invoices/${linkedInv.id}`, invPayload);
      }
      await api.put(`/jobs/${id}`, buildJobPayload({ customer_id: newId }));
      setShowChangeCustomer(false);
      await load();
    } catch (e) { setErr(formatErr(e)); }
  };

  // Job costing breakdown (derived from job_inputs)
  const costing = useMemo(() => {
    const taxRate = hstRate / 100;
    const matCost = 0;
    let materialsCost = 0, miscCost = 0, materialsTax = 0, miscTax = 0;
    for (const it of jobInputs) {
      const sub = Number(it.cost || 0) * Number(it.quantity || 1);
      if (it.kind === "misc") {
        miscCost += sub;
        if (it.has_tax) miscTax += sub * taxRate;
      } else {
        materialsCost += sub;
        if (it.has_tax) materialsTax += sub * taxRate;
      }
    }
    const totalCost = materialsCost + miscCost + materialsTax + miscTax;
    const revenue = Number(linkedInv?.total || 0);
    // HST collected on this job's invoice (already in the invoice total)
    const hstCollected = Number(linkedInv?.hst || 0);
    // Net revenue (excluding HST) is the income that drives corp-tax for this job
    const revenueExHst = revenue - hstCollected;
    const grossProfit = revenue - totalCost;
    // Corp tax on this job's profit (only positive profit is taxed)
    const corpTaxOwed = grossProfit > 0 ? grossProfit * (corpTaxRate / 100) : 0;
    const margin = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
    return {
      materialsCost: materialsCost + materialsTax,
      miscCost: miscCost + miscTax,
      totalCost, revenue, revenueExHst, grossProfit, margin,
      hstCollected, corpTaxOwed, corpTaxRate,
      hasInputs: jobInputs.length > 0,
    };
  }, [jobInputs, hstRate, linkedInv, corpTaxRate]);

  if (!job) return <div className="text-sm text-slate-500">Loading…</div>;

  const status = job.status;
  const completed = status === "Completed";

  return (
    <div data-testid="job-detail" className="space-y-5">
      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded flex items-start gap-2">
          <X size={14} className="mt-0.5 cursor-pointer" onClick={() => setErr("")} />
          {err}
        </div>
      )}

      {/* Breadcrumb */}
      <nav className="text-xs text-slate-500 flex items-center gap-1.5">
        <button onClick={() => nav(-1)} className="p-1 hover:bg-slate-100 rounded"><ArrowLeft size={14} /></button>
        <Link to="/customers" className="hover:text-slate-900">Customers</Link>
        <ChevronRight size={12} />
        <Link to={`/customers/${customer?.id}`} className="hover:text-slate-900">{customer?.name}</Link>
        <ChevronRight size={12} />
        <Link to="/jobs" className="hover:text-slate-900">Jobs</Link>
        <ChevronRight size={12} />
        <span className="text-slate-900 font-medium">Job {job.number}</span>
      </nav>

      {/* Title row */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
              Job for {customer?.name || "—"}
            </h1>
            <button onClick={() => nav(`/schedule?job=${id}`)} className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded" title="Edit"><Edit2 size={14} /></button>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-slate-600 text-sm font-medium">Job {job.number}</span>
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${STATUS_COLOR[status] || "bg-slate-100"}`}>{status}</span>
            {job.priority !== "medium" && <span className="text-xs text-slate-500 capitalize">priority: {job.priority}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {job.status !== "Cancelled" && job.status !== "Completed" && (
            <button
              onClick={() => setDlg({
                title: "Cancel this job?",
                body: "The job will be marked Cancelled and kept in history. You can restore it later from the Jobs list.",
                confirmLabel: "Cancel job",
                danger: false,
                onConfirm: async () => {
                  try {
                    const { data: updated } = await api.post(`/jobs/${id}/cancel`);
                    setJob(updated);
                  } catch (e) { setErr(formatErr(e)); }
                },
              })}
              data-testid="cancel-job-btn"
              className="px-3 py-2 text-amber-700 hover:text-amber-900 hover:bg-amber-50 border border-amber-300 rounded-md text-xs font-medium flex items-center gap-1.5"
            >
              <AlertTriangle size={14}/> Cancel Job
            </button>
          )}
          {job.status === "Cancelled" && (
            <button
              onClick={async () => {
                try {
                  const { data: updated } = await api.post(`/jobs/${id}/uncancel`);
                  setJob(updated);
                } catch (e) { setErr(formatErr(e)); }
              }}
              data-testid="uncancel-job-btn"
              className="px-3 py-2 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 border border-emerald-300 rounded-md text-xs font-medium flex items-center gap-1.5"
            >
              <RotateCcw size={14}/> Restore
            </button>
          )}
          <button onClick={del} data-testid="delete-job-btn" className="p-2 text-slate-400 hover:text-red-600 border border-slate-300 rounded-md"><Trash2 size={16} /></button>
        </div>
      </div>

      {/* Workflow action bar */}
      <WorkflowBar
        job={job} balanceDue={balanceDue} hasInvoice={!!linkedInv}
        busy={busy} onStep={runStep} onReset={resetStep}
      />

      {/* Made-for-you customer intelligence panel — live data from /api/customers/{cid}/summary */}
      {job.customer_id && (
        <MadeForYouPanel customerId={job.customer_id} />
      )}

      {/* Main grid */}
      <div className="grid lg:grid-cols-[320px_1fr] gap-5">
        {/* Left sidebar: Customer + Schedule + Map + widget stubs */}
        <aside className="space-y-4 lg:sticky lg:top-20 self-start">
          <CustomerCard
            customer={customer}
            job={job}
            coords={coords}
            onPickCustomer={async (newId) => {
              try {
                await api.put(`/jobs/${id}`, buildJobPayload({ customer_id: newId }));
                await load();
              } catch (e) { setErr(formatErr(e)); }
            }}
            onChangeCustomer={openChangeCustomer}
          />
          <ScheduleCard job={job} onSave={async (updates) => {
            try {
              await api.put(`/jobs/${id}`, buildJobPayload(updates));
              await load();
            } catch (e) { setErr(formatErr(e)); }
          }} employees={employees} />
          {/* Part 20 (Feb 2026): sidebar cleanup — replaced the row of stub cards
              (Checklists / Tasks / Fields / Job tags / Private notes / Photo reports /
              Lead source) with a single real Attachments widget. Private notes still
              lives at the bottom of the main column. */}
          <SidebarAttachments job={job} reload={load} setDlg={setDlg} />
        </aside>

        {/* Right column */}
        <div className="space-y-5 min-w-0">
          {/* Summary of work */}
          <Section
            title="Summary of work"
            open={openSummary}
            onToggle={() => setOpenSummary((v) => !v)}
            actions={null}
            testid="job-summary-section"
          >
            <FieldTechStatus job={job} employees={employees} open={openTech} onToggle={() => setOpenTech((v) => !v)} />

            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
              <Cell label="Service address" value={
                <span className="flex items-start gap-1.5"><MapPin size={13} className="mt-0.5 text-slate-400 shrink-0" />{job.service_address || customer?.billing_address || "—"}</span>
              } />
              <Cell label="Scheduled" value={fmtDateTime(job.scheduled_start) || "—"} />
              <Cell label="Duration" value={`${job.estimated_duration_minutes || 60} min`} />
              <Cell label="Job type" value={job.job_type || "—"} />
            </div>
            {job.notes && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">Notes</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3">{job.notes}</div>
              </div>
            )}
            <PhotosGallery job={job} reload={load} />
          </Section>

          {/* Invoice section */}
          <Section
            title="Invoice"
            sub={linkedInv ? `#${linkedInv.number}` : "Not yet created"}
            open={true} onToggle={null}
            actions={
              <div className="flex items-center gap-2">
                {linkedInv && (
                  <></>
                )}
                {linkedInv && (
                  <></>
                )}
                {linkedInv && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        const token = localStorage.getItem("token");
                        fetch(fileUrl(`/invoices/${linkedInv.id}/pdf`), { headers: token ? { Authorization: `Bearer ${token}` } : {} })
                          .then((r) => r.blob())
                          .then((blob) => {
                            const href = URL.createObjectURL(blob);
                            const w = window.open(href, "_blank", "noopener,noreferrer");
                            if (w) w.addEventListener("load", () => setTimeout(() => { try { w.print(); } catch (e) { /* user must trigger */ } }, 700));
                            setTimeout(() => URL.revokeObjectURL(href), 60000);
                          });
                      }}
                      data-testid="invoice-print-btn"
                      className="px-2.5 py-1.5 text-slate-500 hover:text-slate-900 border border-slate-300 rounded hover:bg-slate-50"
                      title="Print"
                    >
                      <Printer size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const token = localStorage.getItem("token");
                        fetch(fileUrl(`/invoices/${linkedInv.id}/pdf`), { headers: token ? { Authorization: `Bearer ${token}` } : {} })
                          .then((r) => r.blob())
                          .then((blob) => {
                            const href = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = href;
                            a.download = `invoice-${linkedInv.number || linkedInv.id}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            URL.revokeObjectURL(href);
                          });
                      }}
                      data-testid="invoice-download-pdf-btn"
                      className="px-2.5 py-1.5 text-slate-500 hover:text-slate-900 border border-slate-300 rounded hover:bg-slate-50"
                      title="Download PDF"
                    >
                      <Download size={14} />
                    </button>
                  </>
                )}
                <button
                  onClick={sendInvoice}
                  disabled={!linkedInv || !customer?.email}
                  data-testid="send-invoice-btn"
                  className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 disabled:opacity-50 ${
                    linkedInv && linkedInv.invoice_sent_at
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-[#0F4C81] hover:bg-[#0A365C] text-white"
                  }`}
                  title={linkedInv && linkedInv.invoice_sent_at
                    ? `Last sent ${new Date(linkedInv.invoice_sent_at).toLocaleString("en-CA")} — click to resend`
                    : "Send invoice to customer"}
                >
                  {linkedInv && linkedInv.invoice_sent_at ? (
                    <><Send size={12} /> RESEND · {new Date(linkedInv.invoice_sent_at).toLocaleDateString("en-CA", { day: "2-digit", month: "short" })}</>
                  ) : (
                    <><Send size={12} /> SEND INVOICE</>
                  )}
                </button>
                {/* Part 20 (Feb 2026): manual reminder button. Only visible for
                    already-sent (unpaid) invoices. Uses the same reminder email
                    template as the automated schedule. */}
                {linkedInv && linkedInv.invoice_sent_at && linkedInv.status !== "Paid" && (
                  <button
                    onClick={sendReminder}
                    disabled={remindBusy || !customer?.email}
                    data-testid="send-reminder-btn"
                    className="px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                    title={customer?.email ? "Send payment reminder to customer" : "Customer has no email"}
                  >
                    {remindBusy ? <Loader2 size={12} className="animate-spin"/> : <Bell size={12}/>}
                    SEND REMINDER
                  </button>
                )}
              </div>
            }
            testid="job-invoice-section"
          >
            {linkedInv && (
              <div className="mb-5 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center" data-testid="invoice-summary-header">
                <div className="flex flex-wrap items-baseline gap-6 text-sm">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">Due</div>
                    <div className="mt-0.5 font-medium tabular" style={{ color: settings?.brand_color || "#0F4C81" }}>
                      {linkedInv.due_date
                        ? new Date(linkedInv.due_date).toLocaleDateString("en-CA", { day: "2-digit", month: "short", year: "numeric" })
                        : (linkedInv.payment_terms || "Upon receipt")}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">Auto invoice</div>
                    <div className="mt-0.5 font-medium text-slate-500 dark:text-slate-400">Off</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">Balance</div>
                    <div className="mt-0.5 font-semibold tabular text-slate-900 dark:text-slate-100">{fmtCAD(balanceDue || 0)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">Status</div>
                    <div className="mt-0.5">
                      <span className={`inline-block text-[11px] px-2 py-0.5 rounded ${STATUS_COLOR[linkedInv.status] || "bg-slate-100 text-slate-700"}`}>{linkedInv.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* HST + terms row — appears on customer-facing invoice */}
            {linkedInv && (settings?.hst_number || settings?.invoice_footer_message) && (
              <div data-testid="invoice-hst-terms-row" className="mb-5 flex items-start gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded text-xs text-slate-600 dark:text-slate-400">
                <Edit2 size={13} className="text-[#0F4C81] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0 truncate">
                  {settings?.hst_number && <span className="font-medium text-slate-700 dark:text-slate-300">HST: {settings.hst_number}</span>}
                  {settings?.hst_number && settings?.invoice_footer_message && <span className="mx-2 text-slate-300 dark:text-slate-700">|</span>}
                  {settings?.invoice_footer_message && (
                    <span className="text-slate-500 dark:text-slate-400">{settings.invoice_footer_message}</span>
                  )}
                </div>
              </div>
            )}

            <div data-testid="inline-line-items">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">Line items</div>
              </div>
              <LineItemsEditor
                lineItems={items} setLineItems={setItems}
                invDiscType={discType} setInvDiscType={setDiscType}
                invDiscVal={discVal} setInvDiscVal={setDiscVal}
                hstRate={hstRate}
                brandColor={settings?.brand_color}
              />
            </div>

            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="invoice-autosave-status">
                {saving ? (
                  <><Loader2 size={11} className="animate-spin"/> Saving…</>
                ) : savedAt ? (
                  <><CheckCircle2 size={11} className="text-emerald-600"/> All changes saved · {new Date(savedAt).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" })}</>
                ) : (
                  <>Changes auto-save as you type</>
                )}
              </div>
              <div className="flex gap-2">
                {linkedInv && <Link to={`/invoices/${linkedInv.id}`} className="px-3 py-2 text-xs border border-slate-300 rounded hover:bg-slate-50">Open full invoice</Link>}
                <button
                  onClick={finalizeInvoice}
                  disabled={items.length === 0 || saving}
                  data-testid="finalize-invoice-btn"
                  className="px-4 py-2 text-xs font-medium bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded disabled:opacity-50 flex items-center gap-2"
                  title={linkedInv && linkedInv.invoice_sent_at ? "Open send preview to resend" : "Save + send invoice to customer"}
                >
                  <Send size={12}/>
                  {linkedInv && linkedInv.invoice_sent_at ? "Resend Invoice" : "Finalize & Send Invoice"}
                </button>
              </div>
            </div>
          </Section>

          {/* Estimates section */}
          <Section
            title="Estimates"
            sub={linkedEsts.length === 0 ? "No estimates yet" : `${linkedEsts.length} on file`}
            open={true} onToggle={null}
            actions={
              <Link to={`/estimates/new?customer_id=${customer?.id}`} className="px-3 py-1.5 text-xs border border-[#0F4C81] text-[#0F4C81] rounded hover:bg-[#0F4C81]/5 flex items-center gap-1 font-medium">
                <Plus size={12} /> ESTIMATE
              </Link>
            }
            testid="job-estimates-section"
          >
            {linkedEsts.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6">No estimates linked to this customer.</div>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md overflow-hidden">
                {linkedEsts.map((e) => (
                  <li key={e.id} className="px-4 py-3 hover:bg-slate-50 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/estimates/${e.id}`} className="font-medium text-slate-900 hover:text-[#0F4C81]">{e.number}</Link>
                      <div className="text-xs text-slate-500">{new Date(e.created_at).toLocaleDateString("en-CA", { day: "2-digit", month: "short", year: "numeric" })}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_COLOR[e.status] || "bg-slate-100 text-slate-700"}`}>{e.status}</span>
                      <span className="tabular font-medium text-sm">{fmtCAD(e.total)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Job Inputs (internal cost tracking) */}
          <JobInputsSection
            inputs={jobInputs}
            setInputs={(next) => { setJobInputs(next); setInputsDirty(true); }}
            hstRate={hstRate}
            onSave={saveJobInputs}
            dirty={inputsDirty}
            saving={inputsSaving}
          />

          {/* Job Costing Breakdown (derived) */}
          <JobCostingSection costing={costing} hstRate={hstRate} />

          {/* Part 23 (Feb 2026) — Customer signature capture */}
          <SignaturePad job={job} reload={load} setDlg={setDlg} />

          {/* Private notes + photos (internal only) */}
          <PrivateSection
            notes={privateNotes}
            setNotes={(v) => { setPrivateNotes(v); setNotesDirty(true); }}
            onSaveNotes={savePrivateNotes}
            notesDirty={notesDirty}
            notesSaving={notesSaving}
            privatePhotos={job.private_photos || []}
            jobId={id}
            reload={load}
            setDlg={setDlg}
          />
        </div>
      </div>

      {showChangeCustomer && (
        <ChangeCustomerModal
          customers={allCustomers}
          query={custQuery} setQuery={setCustQuery}
          currentId={job.customer_id}
          onClose={() => setShowChangeCustomer(false)}
          onSelect={changeCustomer}
          busy={changingCust}
        />
      )}

      {showOmwDialog && (
        <OmwDialog
          form={omwForm} setForm={setOmwForm}
          customer={customer}
          onClose={() => setShowOmwDialog(false)}
          onSubmit={submitOmw}
          busy={busy.step === "omw"}
        />
      )}

      {showPayDialog && (
        <JobPayDialog
          form={payForm} setForm={setPayForm}
          balanceDue={balanceDue}
          busy={busy.step === "pay"}
          onClose={() => setShowPayDialog(false)}
          onSubmit={recordPayment}
        />
      )}

      {(job.activity || []).length > 0 && (
        <Section title="Activity" sub={`${(job.activity || []).length} event(s)`} open={true} onToggle={null} testid="job-activity-section">
          <ul className="space-y-2.5 text-sm">
            {[...(job.activity || [])].reverse().map((a) => (
              <li key={a.id} data-testid={`activity-${a.kind}`} className="flex gap-3 items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0F4C81] mt-2 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-700">{a.message}</div>
                  <div className="text-[11px] text-slate-400">{new Date(a.at).toLocaleString("en-CA", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" })}{a.by ? ` · ${a.by}` : ""}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
      <ConfirmDialog dialog={dlg} onClose={() => setDlg(null)} />
    </div>
  );
}

/* ---------- OMW notification dialog ---------- */

function JobPayDialog({ form, setForm, balanceDue, busy, onClose, onSubmit }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={() => !busy && onClose()}>
      <div onClick={(e) => e.stopPropagation()} data-testid="job-pay-dialog" className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-md shadow-xl">
        <header className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <DollarSign size={18}/>
            </div>
            <div>
              <h3 className="font-display text-base font-bold">Record Payment</h3>
              <p className="text-[11px] text-slate-500">Balance due <span className="tabular font-semibold">{fmtCAD(balanceDue)}</span></p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1 text-slate-400 hover:text-slate-900 rounded"><X size={18}/></button>
        </header>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Amount</label>
            <input
              data-testid="job-pay-amount" type="number" step="0.01"
              value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="mt-1 w-full px-3 py-2 text-base border border-slate-300 rounded tabular focus:outline-none focus:border-[#0F4C81]"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Method</label>
            <div className="grid grid-cols-5 gap-1.5 mt-1">
              {[
                { v: "cash", l: "Cash" },
                { v: "cheque", l: "Cheque" },
                { v: "e-transfer", l: "E-Transfer" },
                { v: "credit card", l: "CC" },
                { v: "debit card", l: "Debit" },
              ].map((m) => (
                <button
                  key={m.v} type="button"
                  data-testid={`job-pay-method-${m.v}`}
                  onClick={() => setForm({ ...form, method: m.v })}
                  className={`px-1 py-2 text-xs font-medium border rounded ${form.method === m.v ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                >{m.l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Reference</label>
              <input
                data-testid="job-pay-ref"
                value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="Cheque # / E-tx code"
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Date</label>
              <input
                data-testid="job-pay-date" type="date"
                value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
              />
            </div>
          </div>
          <button
            onClick={onSubmit}
            disabled={busy}
            data-testid="job-pay-submit"
            className="w-full px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin"/> : <DollarSign size={14}/>}
            {busy ? "Recording…" : "Record payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- OMW notification dialog ---------- */

function OmwDialog({ form, setForm, customer, onClose, onSubmit, busy }) {
  const hasEmail = !!customer?.email;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={() => !busy && onClose()}>
      <div onClick={(e) => e.stopPropagation()} data-testid="omw-dialog" className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-md shadow-xl">
        <header className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center"><Truck size={16}/></div>
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">On my way</h3>
              <p className="text-[11px] text-slate-500">Mark en-route{hasEmail ? " + notify customer" : ""}</p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1 text-slate-400 hover:text-slate-900 rounded"><X size={18}/></button>
        </header>
        <div className="px-5 py-4 space-y-4">
          <label className={`flex items-center gap-3 cursor-pointer p-3 rounded border ${form.send_notification ? "bg-indigo-50 border-indigo-300" : "border-slate-200"} ${!hasEmail ? "opacity-50 pointer-events-none" : ""}`}>
            <input
              type="checkbox"
              checked={form.send_notification && hasEmail}
              onChange={(e) => setForm({ ...form, send_notification: e.target.checked })}
              data-testid="omw-send-toggle"
              className="rounded"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900">Send "on my way" email</div>
              <div className="text-xs text-slate-500">{hasEmail ? `→ ${customer.email}` : "Customer has no email on file"}</div>
            </div>
            <Bell size={14} className="text-indigo-500"/>
          </label>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5"><Clock size={11}/> ETA</label>
            <div className="grid grid-cols-4 gap-2 mt-1.5">
              {[10, 20, 30, 60].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setForm({ ...form, eta_minutes: m })}
                  data-testid={`omw-eta-${m}`}
                  className={`px-2 py-2 text-sm border rounded font-medium ${Number(form.eta_minutes) === m ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                >{m} min</button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5"><MessageSquare size={11}/> Note to customer <span className="text-slate-400 normal-case">(optional)</span></label>
            <textarea
              value={form.tech_note}
              onChange={(e) => setForm({ ...form, tech_note: e.target.value })}
              rows={5}
              placeholder="Running 10 min late — see you soon!"
              data-testid="omw-tech-note"
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81] resize-y"
            />
          </div>

          <div className="text-[11px] text-slate-400 italic">SMS notifications coming soon.</div>

          <button
            onClick={onSubmit}
            disabled={busy}
            data-testid="omw-submit-btn"
            className="w-full px-5 py-3 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm font-semibold rounded disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Truck size={14}/>}
            {form.send_notification && hasEmail ? "Send + mark On My Way" : "Mark On My Way"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Subcomponents ---------- */

function WorkflowBar({ job, balanceDue, hasInvoice, busy, onStep, onReset }) {
  const steps = [
    {
      key: "schedule", icon: Calendar, label: "SCHEDULE",
      ts: job.scheduled_start, meta: job.scheduled_start ? "Set" : "Tap edit to set",
      done: !!job.scheduled_start,
      action: null, locked: false,
    },
    {
      key: "omw", icon: Truck, label: "OMW",
      ts: job.omw_at, meta: job.omw_at ? fmtShortTime(job.omw_at) : "On my way",
      done: !!job.omw_at,
      action: () => onStep("omw"),
      // Re-clickable any time before Finish (per field-tech UX request)
      locked: !!job.finished_at,
      alwaysClickable: true,
    },
    {
      key: "start", icon: Play, label: "START",
      ts: job.started_at, meta: job.started_at ? fmtShortTime(job.started_at) : "Begin work",
      done: !!job.started_at,
      action: () => onStep("start"),
      locked: !!job.finished_at,
      alwaysClickable: true,
    },
    {
      key: "finish", icon: CheckCircle2, label: "FINISH",
      ts: job.finished_at, meta: job.finished_at ? fmtShortTime(job.finished_at) : "Wrap up",
      done: !!job.finished_at,
      action: () => onStep("finish"),
      locked: false,
    },
    {
      key: "invoice", icon: FileText, label: "INVOICE",
      ts: job.invoice_sent_at || (hasInvoice && job.invoice_status !== "Draft" ? job.updated_at : null),
      meta: hasInvoice
        ? (job.invoice_status === "Draft" ? "Tap to send" : (job.invoice_status === "Sent" ? "Sent" : job.invoice_status))
        : "Save line items first",
      done: hasInvoice && job.invoice_status && job.invoice_status !== "Draft",
      action: hasInvoice && job.invoice_status === "Draft" ? () => onStep("invoice") : null,
      locked: !hasInvoice || (job.invoice_status && job.invoice_status !== "Draft"),
    },
    {
      key: "pay", icon: DollarSign, label: "PAY",
      ts: hasInvoice && balanceDue === 0 && job.amount_paid > 0 ? job.updated_at : null,
      meta: hasInvoice ? (balanceDue > 0 ? `Due ${fmtCAD(balanceDue)}` : "Paid") : "—",
      done: hasInvoice && balanceDue === 0 && (job.amount_paid || 0) > 0,
      action: hasInvoice && balanceDue > 0 ? () => onStep("pay") : null,
      locked: !hasInvoice || (hasInvoice && balanceDue === 0),
      alwaysClickable: hasInvoice && balanceDue > 0,
    },
  ];

  return (
    <div data-testid="workflow-bar" className="bg-white border border-slate-200 rounded-md p-2 sm:p-3">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {steps.map((s) => (
          <WorkflowStep key={s.key} step={s} busy={busy.step === s.key} onReset={onReset} />
        ))}
      </div>
    </div>
  );
}

function WorkflowStep({ step, busy, onReset }) {
  const Icon = step.icon;
  // Steps with `alwaysClickable` (OMW, Start) stay clickable even when done — as long as not locked
  const clickable = !!step.action && !step.locked && (step.alwaysClickable || !step.done);
  const Cmp = clickable ? "button" : "div";
  const props = clickable
    ? { onClick: step.action, type: "button", "data-testid": `workflow-${step.key}-btn` }
    : { "data-testid": `workflow-${step.key}` };
  return (
    <Cmp
      {...props}
      className={`relative flex flex-col items-center text-center px-2 py-3 rounded-md transition-all group ${
        clickable
          ? "hover:bg-[#0F4C81]/5 cursor-pointer border border-transparent hover:border-[#0F4C81]/30"
          : "cursor-default"
      } ${step.done ? "" : "opacity-90"} ${
        step.key === "pay" && step.done ? "bg-emerald-50 border border-emerald-300" : ""
      }`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-1.5 transition-colors ${
        step.done
          ? (step.key === "pay" ? "bg-emerald-600 text-white ring-2 ring-emerald-300" : "bg-emerald-600 text-white")
          : (clickable ? "bg-[#0F4C81]/10 text-[#0F4C81] group-hover:bg-[#0F4C81] group-hover:text-white" : "bg-slate-100 text-slate-400")
      }`}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />}
      </div>
      <div className={`text-[10px] font-bold tracking-wider ${step.done ? "text-emerald-700" : "text-slate-600"}`}>
        {step.key === "pay" && step.done ? "PAID ✓" : step.label}
      </div>
      <div className={`text-[10px] mt-0.5 truncate w-full ${step.key === "pay" && step.done ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>{step.meta}</div>
      {step.done && step.action && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onReset(step.key); }}
          className="absolute top-1 right-1 p-0.5 text-slate-300 hover:text-slate-700 rounded"
          title={`Reset ${step.label.toLowerCase()}`}
          data-testid={`workflow-${step.key}-reset`}
        >
          <RotateCcw size={11} />
        </button>
      )}
    </Cmp>
  );
}

function ScheduleCard({ job, onSave, employees }) {
  const [editing, setEditing] = useState(false);
  const [anytime, setAnytime] = useState(!job?.scheduled_start);
  const [from, setFrom] = useState(job?.scheduled_start || "");
  const [to, setTo] = useState(job?.scheduled_end || "");
  const [team, setTeam] = useState(job?.assigned_employee_ids || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAnytime(!job?.scheduled_start);
    setFrom(job?.scheduled_start || "");
    setTo(job?.scheduled_end || "");
    setTeam(job?.assigned_employee_ids || []);
  }, [job?.scheduled_start, job?.scheduled_end, job?.assigned_employee_ids]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        scheduled_start: anytime ? null : (from || null),
        scheduled_end: anytime ? null : (to || from || null),
        assigned_employee_ids: team,
      });
      setEditing(false);
    } finally { setSaving(false); }
  };

  const fmtDt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-CA", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
  };

  const fmtForInput = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const off = d.getTimezoneOffset();
      const local = new Date(d.getTime() - off * 60000);
      return local.toISOString().slice(0, 16);
    } catch { return ""; }
  };

  const teamMembers = (employees || []).filter((e) => team.includes(e.id));

  return (
    <div data-testid="schedule-card" className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-display font-semibold text-sm text-slate-900 flex items-center gap-2">
          <Calendar size={14} className="text-[#0F4C81]"/> Schedule
        </h3>
        {!editing ? (
          <button onClick={() => setEditing(true)} data-testid="edit-schedule-btn" className="p-1.5 text-slate-400 hover:text-slate-900 rounded">
            <Edit2 size={13}/>
          </button>
        ) : (
          <button onClick={() => { setEditing(false); setAnytime(!job?.scheduled_start); setFrom(job?.scheduled_start || ""); setTo(job?.scheduled_end || ""); setTeam(job?.assigned_employee_ids || []); }} className="p-1.5 text-slate-400 hover:text-slate-900 rounded">
            <RotateCcw size={13}/>
          </button>
        )}
      </header>
      <div className="px-4 py-3 space-y-3">
        {!editing ? (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">From</div>
              <div className="text-sm text-slate-900 tabular">{anytime ? "Anytime" : fmtDt(from)}</div>
            </div>
            {!anytime && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">To</div>
                <div className="text-sm text-slate-900 tabular">{fmtDt(to)}</div>
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Team</div>
              {teamMembers.length === 0 ? (
                <div className="text-sm text-slate-400 italic">— No team assigned —</div>
              ) : (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {teamMembers.map((m) => (
                    <span key={m.id} className="inline-flex items-center gap-1.5 text-xs bg-amber-100 text-amber-900 px-2 py-1 rounded-full">
                      <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold">{(m.name || "?").charAt(0)}</span>
                      {m.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="schedule-anytime" checked={anytime} onChange={(e) => setAnytime(e.target.checked)}/>
              Anytime (no specific time slot)
            </label>
            {!anytime && (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">From</label>
                  <input data-testid="schedule-from" type="datetime-local" value={fmtForInput(from)} onChange={(e) => setFrom(e.target.value ? new Date(e.target.value).toISOString() : "")} className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">To</label>
                  <input data-testid="schedule-to" type="datetime-local" value={fmtForInput(to)} onChange={(e) => setTo(e.target.value ? new Date(e.target.value).toISOString() : "")} className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"/>
                </div>
              </>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Team</label>
              <select
                data-testid="schedule-team-add"
                value=""
                onChange={(e) => { if (e.target.value && !team.includes(e.target.value)) setTeam([...team, e.target.value]); }}
                className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
              >
                <option value="">+ Add team member…</option>
                {(employees || []).filter((e) => !team.includes(e.id)).map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {teamMembers.map((m) => (
                  <span key={m.id} className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-900 px-2 py-1 rounded-full">
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold">{(m.name || "?").charAt(0)}</span>
                    {m.name}
                    <button data-testid={`remove-team-${m.id}`} onClick={() => setTeam(team.filter((t) => t !== m.id))} className="ml-1 hover:text-amber-700"><X size={10}/></button>
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-schedule-btn"
              className="w-full px-3 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm font-medium rounded disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>}
              Save schedule
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function CustomerCard({ customer, job, coords, onPickCustomer, onChangeCustomer }) {
  // Empty state (Item 13 blank jobs) — show a customer picker in-place with matching card style.
  if (!customer) {
    return (
      <div data-testid="customer-card-empty" className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 pt-4 pb-4 border-b border-slate-100">
          <h3 className="font-display font-semibold text-slate-900 text-sm flex items-center gap-1 mb-3">Customer</h3>
          <p className="text-xs text-slate-500 mb-3">Pick a customer to attach to this job. You can change it later.</p>
          <CustomerPicker
            value=""
            onChange={(id) => onPickCustomer && onPickCustomer(id)}
            placeholder="Search by name, address, phone, or email…"
            testid="jobdetail-customer-picker"
            required
          />
          <Link to="/customers/new" className="mt-3 inline-block text-xs text-[#0F4C81] hover:underline">+ Create a new customer</Link>
        </div>
      </div>
    );
  }
  const addr = job?.service_address || customer.billing_address || "";
  const mapBbox = coords ? `${coords.lng - 0.005},${coords.lat - 0.003},${coords.lng + 0.005},${coords.lat + 0.003}` : null;
  const mapUrl = coords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${mapBbox}&layer=mapnik&marker=${coords.lat},${coords.lng}`
    : null;

  return (
    <div data-testid="customer-card" className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 pt-4 pb-3 border-b border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-slate-900 text-sm flex items-center gap-1">Customer <ChevronDown size={14} className="text-slate-400" /></h3>
          <div className="flex items-center gap-2">
            {onChangeCustomer && (
              <button
                type="button"
                onClick={onChangeCustomer}
                data-testid="change-invoice-customer-btn"
                title="Reassign this job/invoice to a different customer"
                className="text-xs text-slate-500 hover:text-[#0F4C81] px-1.5 py-0.5 border border-slate-200 rounded hover:border-[#0F4C81] flex items-center gap-1"
              >
                <UserCog size={11}/> Reassign
              </button>
            )}
            <Link to={`/customers/${customer.id}`} className="text-xs text-[#0F4C81] hover:underline font-medium">View details</Link>
          </div>
        </div>
        <Link to={`/customers/${customer.id}`} className="font-display font-bold text-slate-900 text-base hover:text-[#0F4C81]">{customer.name}</Link>
        {customer.company_name && <div className="text-xs text-slate-500 mt-0.5">{customer.company_name}</div>}

        <div className="mt-3 space-y-2 text-sm">
          {addr && (
            <div className="flex items-start gap-2 text-slate-600">
              <MapPin size={13} className="mt-0.5 text-slate-400 shrink-0" />
              <span className="break-words">{addr}</span>
            </div>
          )}
          {customer.email && (
            <a href={`mailto:${customer.email}`} className="flex items-center gap-2 text-slate-600 hover:text-[#0F4C81]">
              <Mail size={13} className="text-slate-400 shrink-0" /> {customer.email}
            </a>
          )}
          {customer.phone && (
            <a href={`tel:${customer.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-[#0F4C81]">
              <Phone size={13} className="text-slate-400 shrink-0" /> {customer.phone}
            </a>
          )}
          <div className="flex items-center gap-2 text-xs text-red-600 font-medium">
            <BellOff size={12} /> Notifications off
          </div>
        </div>
      </div>

      <div className="relative bg-slate-100 aspect-[4/3]">
        {mapUrl ? (
          <iframe
            data-testid="customer-map"
            title="Service location map"
            src={mapUrl}
            className="w-full h-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-1">
            <ImageOff size={20} />
            <div className="text-xs">{addr ? "Loading map…" : "No address on file"}</div>
          </div>
        )}
      </div>
      <div className="px-4 py-2 text-[11px] text-red-600 font-medium tracking-wider bg-white border-t border-slate-100">
        LOCATION OFF
      </div>
    </div>
  );
}

function Section({ title, sub, open, onToggle, actions, children, testid }) {
  const isCollapsible = !!onToggle;
  return (
    <section data-testid={testid} className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onToggle || undefined}
          disabled={!isCollapsible}
          className={`flex items-center gap-2 text-left ${isCollapsible ? "hover:opacity-70 cursor-pointer" : "cursor-default"}`}
        >
          <h3 className="font-display font-semibold text-slate-900">{title}</h3>
          {sub && <span className="text-xs text-slate-500">· {sub}</span>}
          {isCollapsible && (open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />)}
        </button>
        {actions}
      </header>
      {(!isCollapsible || open) && <div className="px-5 py-4">{children}</div>}
    </section>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="mt-0.5 text-slate-900">{value}</div>
    </div>
  );
}

function FieldTechStatus({ job, employees, open, onToggle }) {
  const assigned = (job.assigned_employee_ids || []).map((eid) => employees.find((e) => e.id === eid)).filter(Boolean);
  const timeEntries = job.time_entries || [];
  const totalsByEmp = {};
  for (const t of timeEntries) {
    if (!t.start_at || !t.end_at) continue;
    const h = Math.max((new Date(t.end_at) - new Date(t.start_at)) / 3600000, 0);
    const key = t.employee_id || "_self";
    totalsByEmp[key] = (totalsByEmp[key] || 0) + h;
  }

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors">
        <span className="font-medium text-sm text-slate-900">Field tech status</span>
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="field-tech-status-table">
            <thead className="bg-white text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-100">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Employee</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">Hours on job</th>
                <th className="px-4 py-2 text-right font-medium">Labour cost</th>
              </tr>
            </thead>
            <tbody>
              {assigned.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-4 text-center text-slate-400 text-sm">No technicians assigned.</td></tr>
              )}
              {assigned.map((e) => {
                const h = totalsByEmp[e.id] || 0;
                const rate = Number(e.hourly_rate || 0);
                return (
                  <tr key={e.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#E0F2FE] text-[#0F4C81] text-[10px] flex items-center justify-center font-bold">
                          {(e.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                        </div>
                        <span className="font-medium">{e.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">
                      {h > 0 ? "Worked" : "Assigned"}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular">{h.toFixed(2)}h</td>
                    <td className="px-4 py-2.5 text-right tabular">{fmtCAD(h * rate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PhotosGallery({ job, reload }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/jobs/${job.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
    } catch (e) { alert(formatErr(e)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const photos = job.photos || [];
  return (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Photos ({photos.length})</div>
        <label className="px-2.5 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 cursor-pointer flex items-center gap-1">
          <Camera size={12} /> {uploading ? "Uploading…" : "Add"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
      {photos.length === 0 ? (
        <div className="text-xs text-slate-400 italic">No photos yet.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {photos.map((p) => (
            <a key={p.id} href={fileUrl(`/job-photos/${p.id}/download`)} target="_blank" rel="noopener noreferrer" className="aspect-square block border border-slate-200 rounded overflow-hidden hover:border-slate-400">
              <img src={fileUrl(`/job-photos/${p.id}/download`)} alt="" className="w-full h-full object-cover" loading="lazy" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}


/* ---------- Job Inputs (private costs) ---------- */

function JobInputsSection({ inputs, setInputs, hstRate, onSave, dirty, saving }) {
  const [open, setOpen] = useState(true);
  const materials = inputs.filter((i) => i.kind !== "misc");
  const misc = inputs.filter((i) => i.kind === "misc");

  const subtotalFor = (list, includeTax) => list.reduce((sum, it) => {
    const sub = Number(it.cost || 0) * Number(it.quantity || 1);
    return sum + sub + (includeTax && it.has_tax ? sub * (hstRate / 100) : 0);
  }, 0);
  const matSub = subtotalFor(materials, true);
  const miscSub = subtotalFor(misc, true);

  const addRow = (kind) => {
    setInputs([...inputs, {
      id: crypto.randomUUID(), name: "", description: "",
      cost: 0, quantity: 1, has_tax: false, kind,
    }]);
  };
  const update = (id, patch) => {
    setInputs(inputs.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };
  const remove = (id) => setInputs(inputs.filter((it) => it.id !== id));

  return (
    <section data-testid="job-inputs-section" className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 bg-slate-50/70">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 hover:opacity-70">
          <Calculator size={15} className="text-slate-500" />
          <h3 className="font-display font-semibold text-slate-900">Job inputs</h3>
          <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold bg-amber-100 px-1.5 py-0.5 rounded">INTERNAL</span>
          {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
        </button>
        {dirty && (
          <button onClick={onSave} disabled={saving} data-testid="save-job-inputs-btn" className="px-3 py-1.5 text-xs font-medium bg-amber-700 hover:bg-amber-800 text-white rounded disabled:opacity-50 flex items-center gap-1.5">
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save inputs
          </button>
        )}
      </header>
      {open && (
        <div className="px-5 py-4 space-y-5">
          <p className="text-xs text-slate-500">Job inputs will not be shown on the customer invoice, or change the price of the job. Use this to track your true material cost for profit analysis.</p>

          <InputGroup
            label="Materials"
            items={materials}
            addLabel="Add material"
            onAdd={() => addRow("material")}
            onUpdate={update}
            onRemove={remove}
            subtotal={matSub}
            hstRate={hstRate}
            testid="job-inputs-materials"
          />

          <InputGroup
            label="Miscellaneous"
            items={misc}
            addLabel="Add miscellaneous"
            onAdd={() => addRow("misc")}
            onUpdate={update}
            onRemove={remove}
            subtotal={miscSub}
            hstRate={hstRate}
            testid="job-inputs-misc"
          />
        </div>
      )}
    </section>
  );
}

function InputGroup({ label, items, addLabel, onAdd, onUpdate, onRemove, subtotal, hstRate, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</div>
      {items.length > 0 && (
        <>
        {/* Mobile stacked view (< 640px) */}
        <div className="sm:hidden border border-slate-200 rounded-md overflow-hidden mb-2 divide-y divide-slate-100 bg-white">
          {items.map((it) => {
            const lineCost = Number(it.cost || 0) * Number(it.quantity || 1);
            const withTax = it.has_tax ? lineCost * (1 + hstRate / 100) : lineCost;
            return (
              <div key={it.id} className="p-3 space-y-3" data-testid={`${testid}-row-m-${it.id}`}>
                <input
                  value={it.name}
                  onChange={(e) => onUpdate(it.id, { name: e.target.value })}
                  placeholder="Description"
                  className="w-full px-3 py-2 text-base border border-slate-200 rounded focus:outline-none focus:border-[#0F4C81] bg-white"
                />
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">Qty</span>
                    <input
                      type="number" min="0" step="0.01" inputMode="decimal"
                      value={it.quantity}
                      onChange={(e) => onUpdate(it.id, { quantity: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 text-base text-right border border-slate-200 rounded focus:outline-none focus:border-[#0F4C81] tabular bg-white"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">Unit cost</span>
                    <input
                      type="number" min="0" step="0.01" inputMode="decimal"
                      value={it.cost}
                      onChange={(e) => onUpdate(it.id, { cost: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 text-base text-right border border-slate-200 rounded focus:outline-none focus:border-[#0F4C81] tabular bg-white"
                    />
                  </label>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => onUpdate(it.id, { has_tax: !it.has_tax })}
                    data-testid={`tax-toggle-m-${it.id}`}
                    className={`text-xs uppercase tracking-wider font-bold px-3 py-2 rounded transition-colors min-h-[44px] ${
                      it.has_tax
                        ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                  >
                    {it.has_tax ? `+HST ${hstRate}%` : "No tax"}
                  </button>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Line cost</div>
                    <div className="text-base font-semibold tabular text-slate-900">{fmtCAD(withTax)}</div>
                  </div>
                </div>
                <div className="pt-1 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => onRemove(it.id)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded min-h-[44px]"
                  >
                    <X size={14}/> Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {/* Desktop table view (>= 640px) — unchanged */}
        <div className="hidden sm:block border border-slate-200 rounded-md overflow-hidden mb-2">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Item</th>
                <th className="text-right px-3 py-2 font-medium w-16">Qty</th>
                <th className="text-right px-3 py-2 font-medium w-24">Unit cost</th>
                <th className="text-center px-3 py-2 font-medium w-20">Tax</th>
                <th className="text-right px-3 py-2 font-medium w-24">Line cost</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const lineCost = Number(it.cost || 0) * Number(it.quantity || 1);
                const withTax = it.has_tax ? lineCost * (1 + hstRate / 100) : lineCost;
                return (
                  <tr key={it.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <input
                        value={it.name}
                        onChange={(e) => onUpdate(it.id, { name: e.target.value })}
                        placeholder="Description"
                        className="w-full bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number" min="0" step="0.01"
                        value={it.quantity}
                        onChange={(e) => onUpdate(it.id, { quantity: parseFloat(e.target.value) || 0 })}
                        className="w-14 bg-transparent text-sm text-right tabular focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number" min="0" step="0.01"
                        value={it.cost}
                        onChange={(e) => onUpdate(it.id, { cost: parseFloat(e.target.value) || 0 })}
                        className="w-20 bg-transparent text-sm text-right tabular focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => onUpdate(it.id, { has_tax: !it.has_tax })}
                        data-testid={`tax-toggle-${it.id}`}
                        title={it.has_tax ? `HST ${hstRate}% included` : "Add HST to this line"}
                        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded transition-colors ${
                          it.has_tax
                            ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                        }`}
                      >
                        {it.has_tax ? `+HST` : "No tax"}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right text-sm tabular text-slate-900">{fmtCAD(withTax)}</td>
                    <td className="text-center">
                      <button type="button" onClick={() => onRemove(it.id)} className="text-slate-300 hover:text-red-600 p-1"><X size={14}/></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={onAdd}
          data-testid={`${testid}-add-btn`}
          className="text-sm text-[#0F4C81] hover:underline flex items-center gap-1.5 font-medium min-h-[44px] sm:min-h-0"
        >
          <Plus size={14}/> {addLabel}
        </button>
        <div className="text-sm">
          <span className="text-slate-500 mr-2">Cost subtotal</span>
          <span className="tabular font-semibold text-slate-900">{fmtCAD(subtotal)}</span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Job Costing Breakdown ---------- */

function JobCostingSection({ costing, hstRate }) {
  const [open, setOpen] = useState(true);
  const { revenue, totalCost, grossProfit, margin, hasInputs, hstCollected, corpTaxOwed, corpTaxRate } = costing;
  const profitColor = grossProfit >= 0 ? "#10B981" : "#E11D48";

  // Pie chart segments (CSS conic gradient)
  const profitPct = revenue > 0 ? Math.max(0, Math.min(100, (grossProfit / revenue) * 100)) : 100;
  const costPct = revenue > 0 ? 100 - profitPct : 0;

  return (
    <section data-testid="job-costing-section" className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 hover:opacity-70">
          <PieIcon size={15} className="text-slate-500"/>
          <h3 className="font-display font-semibold text-slate-900">Job costing breakdown</h3>
          <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold bg-amber-100 px-1.5 py-0.5 rounded">INTERNAL</span>
          {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
        </button>
      </header>
      {open && (
        <div className="px-5 py-5">
          {!hasInputs && revenue === 0 ? (
            <div className="text-sm text-slate-500 italic text-center py-6">
              Add job inputs and create an invoice to see profitability.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <Stat
                  label="Profit margin"
                  value={revenue > 0 ? `${margin.toFixed(0)}%` : "—"}
                  color={profitColor}
                  testid="costing-margin"
                />
                <Stat
                  label="Total revenue"
                  value={fmtCAD(revenue)}
                  testid="costing-revenue"
                />
                <Stat
                  label="Total job costs"
                  value={fmtCAD(totalCost)}
                  color="#E11D48"
                  dot
                  testid="costing-cost"
                />
                <Stat
                  label="Total gross profit"
                  value={fmtCAD(grossProfit)}
                  color={profitColor}
                  dot
                  testid="costing-profit"
                />
              </div>

              {revenue > 0 && (
                <div className="flex flex-col sm:flex-row gap-6 items-center">
                  <div
                    className="relative w-32 h-32 rounded-full shrink-0"
                    style={{
                      background: `conic-gradient(${profitColor} 0% ${profitPct}%, #E2E8F0 ${profitPct}% 100%)`,
                    }}
                  >
                    <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center">
                      <div className="text-lg font-bold tabular" style={{ color: profitColor }}>{margin.toFixed(0)}%</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">margin</div>
                    </div>
                  </div>
                  <div className="flex-1 space-y-3 text-sm">
                    <LegendRow color={profitColor} label="Gross profit" value={fmtCAD(grossProfit)} pct={`${profitPct.toFixed(0)}% of revenue`} />
                    <LegendRow color="#E2E8F0" label="Job costs" value={fmtCAD(totalCost)} pct={`${costPct.toFixed(0)}% of revenue`} />
                  </div>
                </div>
              )}

              <div className="mt-5 text-[11px] text-slate-500 italic">
                Cost includes job inputs with HST @ {hstRate}% applied to items marked +HST.
              </div>
            </>
          )}

          {/* Item 7: HST + Corp Tax liability tiles — always shown so accountants can see zeroes too */}
          <div className="mt-5 pt-5 border-t border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] uppercase tracking-wider text-rose-700 font-bold bg-rose-100 px-1.5 py-0.5 rounded">Tax liability</span>
              <span className="text-xs text-slate-600">attributable to this job</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div data-testid="job-hst-collected" className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">HST collected</div>
                <div className="font-display text-lg font-bold tabular text-emerald-900">{fmtCAD(hstCollected)}</div>
                <div className="text-[10px] text-emerald-700 mt-0.5">on customer invoice ({hstRate}%)</div>
              </div>
              <div data-testid="job-hst-owed" className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">HST owed to CRA</div>
                <div className="font-display text-lg font-bold tabular text-rose-900">{fmtCAD(hstCollected)}</div>
                <div className="text-[10px] text-rose-700 mt-0.5">remit on next return</div>
              </div>
              <div data-testid="job-corp-tax-owed" className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Corp tax on profit</div>
                <div className="font-display text-lg font-bold tabular text-amber-900">{fmtCAD(corpTaxOwed)}</div>
                <div className="text-[10px] text-amber-700 mt-0.5">ON CCPC @ {corpTaxRate}%</div>
              </div>
            </div>
            <div className="mt-3 text-[11px] text-slate-500 italic">
              HST collected is also the HST owed for this single job (no ITCs allocated here — they reduce the global liability on My Money). Corp tax shown is only on positive gross profit.
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, color, testid, dot }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5">
        {dot && <span className="w-2 h-2 rounded-full inline-block" style={{ background: color || "#0F4C81" }} />}
        {label}
      </div>
      <div className="text-xl font-display font-bold tabular mt-1" style={{ color: color || "#0F172A" }}>{value}</div>
    </div>
  );
}
function LegendRow({ color, label, value, pct }) {
  return (
    <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-100 last:border-b-0 last:pb-0">
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: color }} />
        <span className="text-slate-700 font-medium">{label}</span>
      </div>
      <div className="text-right">
        <div className="font-semibold tabular text-slate-900">{value}</div>
        <div className="text-[11px] text-slate-500">{pct}</div>
      </div>
    </div>
  );
}

/* ---------- Private Notes + Photos ---------- */

function PrivateSection({ notes, setNotes, onSaveNotes, notesDirty, notesSaving, privatePhotos, jobId, reload, setDlg }) {
  const [open, setOpen] = useState(true);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/jobs/${jobId}/private-photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
    } catch (err) {
      alert(formatErr(err));
    } finally { setUploading(false); e.target.value = ""; }
  };

  // Part 21 (Feb 2026): download + delete for individual private photos.
  const downloadPhoto = (p) => {
    // Trigger a filename-preserving download by fetching + blob.
    const url = fileUrl(`/job-photos/${p.id}/download`);
    // Use an anchor with `download` attribute; the auth token is baked into
    // fileUrl() via ?auth=… so no headers needed. Blob-download would also work
    // but this is simpler and works cross-origin because the backend sends
    // Content-Disposition: attachment on this route.
    const a = document.createElement("a");
    a.href = url;
    a.download = p.filename || `private-photo-${p.id}.jpg`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const deletePhoto = (p) => {
    setDlg?.({
      title: "Delete private photo?",
      body: `"${p.filename || "This photo"}" will be permanently removed from this job. This action cannot be undone.`,
      confirmLabel: "Delete",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/job-photos/${p.id}`);
          await reload();
        } catch (err) { alert(formatErr(err)); }
      },
    });
  };

  return (
    <section data-testid="private-section" className="bg-amber-50/40 border border-amber-200 rounded-md overflow-hidden">
      <header className="px-5 py-3 border-b border-amber-200 flex items-center justify-between bg-amber-50">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 hover:opacity-70">
          <Lock size={14} className="text-amber-700"/>
          <h3 className="font-display font-semibold text-amber-900">Private notes & photos</h3>
          <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold bg-amber-100 px-1.5 py-0.5 rounded">ONLY YOU CAN SEE THIS</span>
          {open ? <ChevronDown size={16} className="text-amber-700" /> : <ChevronRight size={16} className="text-amber-700" />}
        </button>
      </header>
      {open && (
        <div className="px-5 py-4 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium uppercase tracking-wider text-amber-900">Private notes</label>
              <div className="flex items-center gap-2">
                {notesDirty && (
                  <button onClick={onSaveNotes} disabled={notesSaving} data-testid="save-job-private-notes-btn" className="px-3 py-1 text-xs bg-amber-700 hover:bg-amber-800 text-white rounded disabled:opacity-50 flex items-center gap-1">
                    {notesSaving ? <Loader2 size={11} className="animate-spin"/> : <Save size={11}/>}
                    Save
                  </button>
                )}
              </div>
            </div>
            <textarea
              data-testid="job-private-notes-input"
              name="private_notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              placeholder="Internal notes for this job — never shared with the customer."
              className="w-full px-3 py-2 text-sm bg-white border border-amber-200 rounded focus:outline-none focus:border-amber-500 resize-y"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium uppercase tracking-wider text-amber-900">Private photos ({privatePhotos.length})</label>
              <label data-testid="upload-private-photo-btn" className="px-2.5 py-1 text-xs border border-amber-300 bg-white text-amber-900 rounded hover:bg-amber-100 cursor-pointer flex items-center gap-1">
                <Camera size={12} /> {uploading ? "Uploading…" : "Add private photo"}
                <input type="file" accept="image/*" className="hidden" onChange={upload} />
              </label>
            </div>
            {privatePhotos.length === 0 ? (
              <div className="text-xs text-amber-700/70 italic">No private photos yet. These are visible only to you, never to the customer.</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {privatePhotos.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`private-photo-${p.id}`}
                    className="group aspect-square block border border-amber-300 rounded overflow-hidden hover:border-amber-500 relative bg-amber-50"
                  >
                    <a
                      href={fileUrl(`/job-photos/${p.id}/download`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full h-full"
                      title={p.filename || "Open"}
                    >
                      <img src={fileUrl(`/job-photos/${p.id}/download`)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </a>
                    <span className="absolute bottom-1 right-1 bg-amber-700 text-white text-[8px] font-bold uppercase tracking-wider px-1 rounded pointer-events-none">Private</span>
                    {/* Part 21 (Feb 2026): download + delete controls on hover */}
                    <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); downloadPhoto(p); }}
                        title="Download"
                        data-testid={`private-photo-download-${p.id}`}
                        className="p-1 bg-white/95 text-amber-800 rounded hover:bg-amber-100"
                      >
                        <Download size={11}/>
                      </button>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); deletePhoto(p); }}
                        title="Delete"
                        data-testid={`private-photo-delete-${p.id}`}
                        className="p-1 bg-white/95 text-rose-600 rounded hover:bg-rose-50"
                      >
                        <Trash2 size={11}/>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- Change Customer Modal ---------- */

function ChangeCustomerModal({ customers, query, setQuery, currentId, onClose, onSelect, busy }) {
  const filtered = customers.filter((c) => {
    if (c.id === currentId) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || (c.company_name || "").toLowerCase().includes(q);
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div onClick={(e) => e.stopPropagation()} data-testid="change-customer-modal" className="bg-white rounded-lg w-full max-w-md shadow-xl flex flex-col max-h-[80vh]">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-100 text-[#0F4C81] flex items-center justify-center shrink-0"><UserCog size={18}/></div>
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Reassign to a different customer</h3>
              <p className="text-xs text-slate-500 mt-0.5">Both the invoice and this job will move to the selected customer. Number, line items, and history are kept.</p>
            </div>
          </div>
          <input
            data-testid="customer-search-input"
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, company, or email"
            className="mt-3 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
          />
        </div>
        <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {filtered.length === 0 && <li className="px-5 py-6 text-center text-sm text-slate-400">No matches.</li>}
          {filtered.slice(0, 50).map((c) => (
            <li key={c.id}>
              <button
                onClick={() => onSelect(c.id)}
                disabled={busy}
                data-testid={`pick-customer-${c.id}`}
                className="w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center justify-between disabled:opacity-50"
              >
                <div>
                  <div className="font-medium text-slate-900 text-sm">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.email || c.phone || c.company_name || "—"}</div>
                </div>
                <ChevronRight size={14} className="text-slate-400"/>
              </button>
            </li>
          ))}
        </ul>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 disabled:opacity-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}
